// Черновики вложений композера (ADR-115): локальный список, загрузка, ошибки, лимиты.

import { create } from 'zustand'
import { uploadSessionAttachment } from '@/lib/api/attachments'
import { getDraftAttachmentStorage } from '@/lib/draft-attachments'
import {
  DEFAULT_ATTACHMENT_LIMITS,
  MAX_FILE_BYTES,
  MAX_FILES_PER_MESSAGE,
  loadAttachmentLimits,
  type AttachmentLimits,
} from '@/lib/attachments/limits'

// Совместимость: тесты (и внешние потребители) импортируют константы из стора.
export { MAX_FILE_BYTES, MAX_FILES_PER_MESSAGE }

/** Состояние загрузки одного элемента композера. */
export type AttachmentUploadState = 'pending' | 'uploading' | 'ready' | 'error'

/** Причина отказа — отображается в чипе и переводится через i18n. */
export type AttachmentErrorReason = 'too_many' | 'too_large' | 'upload_failed'

/** Элемент композера: файл + локальный предпросмотр + результат загрузки. */
export interface DraftAttachment {
  /** Локальный id (чип существует до и после загрузки). */
  id: string
  /** Исходный файл; у восстановленных из истории (`restore`) его нет. */
  file?: File
  name: string
  mimeType: string
  size: number
  /** Локальный object URL для изображений (иначе `''`); у восстановленных — URL контента. */
  previewUrl: string
  /** Группа элементов из одного источника — удаление каскадное. */
  groupId?: string
  /** Сколько элементов в группе (для каскадного удаления и подписей). */
  groupSize?: number
  upload: AttachmentUploadState
  error?: AttachmentErrorReason
  /** Отправлено байт (runtime-only; для подписи прогресса). */
  uploadedBytes?: number
  /** Доля загрузки 0..1 (runtime-only; `undefined` — прогресс неизвестен). */
  progress?: number
  /**
   * Фактический предел файла из `413 details.maxBytesPerFile` (может быть
   * меньше UX-фоллбека). Runtime-only: в IndexedDB не персистится.
   */
  maxBytes?: number
  /** id серверного вложения после успешной загрузки. */
  partId?: string
  /** Content-addressed путь blob-порта после успешной загрузки. */
  blobPath?: string
}

/** Вложение, восстановленное в композер из истории сообщения (`retry`). */
export interface RestorableAttachment {
  blobPath: string
  mimeType: string
  size: number
  name?: string
  partId: string
  previewUrl?: string
}

interface AttachmentsState {
  bySession: Record<string, DraftAttachment[]>
  /** Эффективные лимиты инстанса (`GET /api/v2/meta`); до ответа — UX-фоллбеки. */
  limits: AttachmentLimits
  /** Загрузить лимиты с сервера (кэш 60 с и дедуп — в `loadAttachmentLimits`). */
  loadLimits: () => Promise<void>
  /** Подменить лимиты (тесты/транспорт). */
  setLimits: (limits: AttachmentLimits) => void
  /** Добавить файлы и сразу начать их загрузку. */
  addFiles: (sessionId: string, files: File[], opts?: { groupId?: string }) => Promise<void>
  /** Удалить элемент; у элемента с `groupId` удаляется вся группа. */
  remove: (sessionId: string, id: string) => void
  /** Очистить композер сессии, прервав активные загрузки. */
  clear: (sessionId: string) => void
  /** Повторить загрузку элемента, упавшего с ошибкой. */
  retry: (sessionId: string, id: string) => Promise<void>
  /**
   * Восстановить готовые вложения из истории сообщения («Повторить»): у них
   * нет исходного `File`, но есть серверный `blobPath` (он же `partId`).
   */
  restore: (sessionId: string, items: RestorableAttachment[]) => void
  /** id успешно загруженных вложений — тело запроса отправки сообщения. */
  readyPartIds: (sessionId: string) => string[]
  /** Есть незавершённые загрузки — отправку блокируем. */
  hasPending: (sessionId: string) => boolean
  /** Есть элементы с ошибкой — отправку блокируем до удаления/повтора. */
  hasFailed: (sessionId: string) => boolean
  /**
   * Восстановить черновики сессии из хранилища байтов (reload/перемонтирование).
   * Идемпотентно: повторный вызов для той же сессии — no-op.
   */
  hydrate: (sessionId: string) => Promise<void>
  /** Освободить object URL при размонтировании композера. */
  dispose: (sessionId: string) => void
}

/** Активные загрузки: ключ `${sessionId}::${id}`. Модульный скоуп — вне React. */
const controllers = new Map<string, AbortController>()

/**
 * Минимальный интервал обновления прогресса в сторе (~10 раз/с): событие
 * `xhr.upload.onprogress` приходит очень часто, и каждый `set` — ререндер
 * всего списка вложений. Последнее значение (файл отправлен) эмитится всегда.
 */
const PROGRESS_EMIT_INTERVAL_MS = 100

/** Сессии, черновики которых уже подняты из хранилища байтов (защита от повторов). */
const hydrated = new Set<string>()

/** Счётчик для фоллбек-id, когда `crypto.randomUUID` недоступен. */
let localIdCounter = 0

function controllerKey(sessionId: string, id: string): string {
  return `${sessionId}::${id}`
}

function createLocalId(): string {
  const uuid = globalThis.crypto?.randomUUID?.()
  if (uuid) return `att_${uuid}`
  localIdCounter += 1
  return `att_${Date.now().toString(36)}_${localIdCounter}`
}

function isImage(mimeType: string): boolean {
  return mimeType.toLowerCase().split(';', 1)[0]?.trim().startsWith('image/') ?? false
}

/** Локальный предпросмотр только для изображений (jsdom/старые браузеры — без него). */
function createPreviewUrl(file: File, mimeType: string): string {
  if (!isImage(mimeType)) return ''
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return ''
  try {
    return URL.createObjectURL(file)
  } catch {
    return ''
  }
}

/** Предпросмотр восстановленного из хранилища изображения (Blob → object URL). */
function createPreviewUrlFromBlob(blob: Blob, mimeType: string): string {
  if (!isImage(mimeType)) return ''
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return ''
  try {
    return URL.createObjectURL(blob)
  } catch {
    return ''
  }
}

function revokePreviewUrl(item: DraftAttachment): void {
  if (!item.previewUrl) return
  // Освобождаем только локальные object URL: у восстановленных вложений
  // `previewUrl` — обычный HTTP-адрес контента, его отзывать нельзя.
  if (!item.previewUrl.startsWith('blob:')) return
  if (typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') return
  try {
    URL.revokeObjectURL(item.previewUrl)
  } catch {
    // Освобождение URL — best-effort: ошибка не должна ломать состояние.
  }
}

function abortUpload(sessionId: string, id: string): void {
  const key = controllerKey(sessionId, id)
  const controller = controllers.get(key)
  if (!controller) return
  controller.abort()
  controllers.delete(key)
}

function makeDraft(file: File, groupId?: string): DraftAttachment {
  const mimeType = file.type || 'application/octet-stream'
  return {
    id: createLocalId(),
    file,
    name: file.name,
    mimeType,
    size: file.size,
    previewUrl: createPreviewUrl(file, mimeType),
    groupId,
    upload: 'pending',
  }
}

export const useAttachmentsStore = create<AttachmentsState>()((set, get) => {
  /** Патч одного элемента; если элемент уже удалён — тихо игнорируем. */
  function patch(sessionId: string, id: string, changes: Partial<DraftAttachment>): void {
    set((state) => {
      const list = state.bySession[sessionId]
      if (!list) return state
      const index = list.findIndex((item) => item.id === id)
      if (index < 0) return state
      const next = list.slice()
      next[index] = { ...list[index]!, ...changes }
      return { bySession: { ...state.bySession, [sessionId]: next } }
    })
  }

  /** Загрузка элемента: `pending` → `uploading` → `ready` / `error`. */
  async function upload(sessionId: string, id: string): Promise<void> {
    const item = get().bySession[sessionId]?.find((entry) => entry.id === id)
    if (!item || !item.file) return
    const file = item.file

    const controller = new AbortController()
    controllers.set(controllerKey(sessionId, id), controller)
    patch(sessionId, id, {
      upload: 'uploading',
      error: undefined,
      maxBytes: undefined,
      uploadedBytes: 0,
      progress: 0,
    })

    // Троттлинг прогресса: первый кадр показываем сразу, дальше — не чаще
    // `PROGRESS_EMIT_INTERVAL_MS`, финальный (100%) — всегда.
    let lastProgressAt = 0

    try {
      const uploaded = await uploadSessionAttachment(sessionId, file, {
        signal: controller.signal,
        onProgress: (loaded, total) => {
          const now = Date.now()
          const complete = total > 0 && loaded >= total
          if (!complete && now - lastProgressAt < PROGRESS_EMIT_INTERVAL_MS) return
          lastProgressAt = now
          patch(sessionId, id, {
            uploadedBytes: loaded,
            progress: total > 0 ? Math.min(1, Math.max(0, loaded / total)) : 0,
          })
        },
      })
      if (controller.signal.aborted) {
        // Отмена (clear/remove) — элемент уже уходит из состояния.
        patch(sessionId, id, { uploadedBytes: undefined, progress: undefined })
        return
      }
      const mimeType = uploaded.mime || item.mimeType
      const size = uploaded.size || item.size
      const name = uploaded.name || item.name
      patch(sessionId, id, {
        upload: 'ready',
        error: undefined,
        maxBytes: undefined,
        // Прогресс — runtime-подпись `uploading`: у готового элемента её нет.
        uploadedBytes: undefined,
        progress: undefined,
        partId: uploaded.id,
        blobPath: uploaded.blobPath,
        mimeType,
        size,
        name,
      })
      // Байты уже в хранилище — фиксируем результат загрузки, чтобы после
      // reload черновик поднялся сразу готовым (`ready`).
      void getDraftAttachmentStorage()
        .patch(sessionId, id, {
          partId: uploaded.id,
          blobPath: uploaded.blobPath,
          mimeType,
          size,
          name,
          error: undefined,
        })
        .catch(() => {})
    } catch (err) {
      // Отмена (clear/remove) — не ошибка: элемент уже уходит из состояния.
      if (controller.signal.aborted) {
        patch(sessionId, id, { uploadedBytes: undefined, progress: undefined })
        return
      }
      // 413 — серверный лимит файла (может быть меньше локального UX-предела).
      // `details.maxBytesPerFile` — фактический предел инстанса: по нему
      // показываем текст ошибки, а не по фоллбеку.
      const status = (err as { status?: number } | null)?.status
      const reason: AttachmentErrorReason = status === 413 ? 'too_large' : 'upload_failed'
      const serverMax = (err as { details?: { maxBytesPerFile?: number } } | null)?.details
        ?.maxBytesPerFile
      patch(sessionId, id, {
        upload: 'error',
        error: reason,
        uploadedBytes: undefined,
        progress: undefined,
        maxBytes:
          status === 413 && typeof serverMax === 'number' && serverMax > 0 ? serverMax : undefined,
      })
      void getDraftAttachmentStorage()
        .patch(sessionId, id, { error: reason })
        .catch(() => {})
    } finally {
      const key = controllerKey(sessionId, id)
      if (controllers.get(key) === controller) controllers.delete(key)
    }
  }

  return {
    bySession: {},
    limits: DEFAULT_ATTACHMENT_LIMITS,

    async loadLimits() {
      const limits = await loadAttachmentLimits()
      set({ limits })
    },

    setLimits(limits) {
      set({ limits })
    },

    async addFiles(sessionId, files, opts) {
      if (!files.length) return
      const groupId = opts?.groupId
      // Снимок читаем до `set`: дедуп и лимиты считать по актуальному списку,
      // а создавать object URL — вне обновления состояния.
      const current = get().bySession[sessionId] ?? []
      const limits = get().limits
      const drafts: DraftAttachment[] = []
      let accepted = current.length

      for (const file of files) {
        const duplicate =
          current.some((item) => item.name === file.name && item.size === file.size) ||
          drafts.some((item) => item.name === file.name && item.size === file.size)
        if (duplicate) continue

        const draft = makeDraft(file, groupId)
        if (file.size > limits.maxBytesPerFile) {
          // Превышение размера — элемент не загружается, но причина остаётся в чипе.
          draft.upload = 'error'
          draft.error = 'too_large'
        } else if (accepted >= limits.maxFilesPerMessage) {
          draft.upload = 'error'
          draft.error = 'too_many'
        } else {
          accepted += 1
        }
        drafts.push(draft)
      }

      if (!drafts.length) return

      if (groupId) {
        const groupSize = current.filter((item) => item.groupId === groupId).length + drafts.length
        for (const draft of drafts) draft.groupSize = groupSize
      }

      set((state) => ({
        bySession: {
          ...state.bySession,
          [sessionId]: [...(state.bySession[sessionId] ?? []), ...drafts],
        },
      }))

      // Байты сразу в хранилище: reload/выгрузка PWA не должны терять вложения
      // (включая отклонённые по лимиту — пользователь видит причину в чипе).
      const storage = getDraftAttachmentStorage()
      const persistence = drafts.map((draft) =>
        storage
          .put({
            sessionId,
            draftId: draft.id,
            name: draft.name,
            mimeType: draft.mimeType,
            size: draft.size,
            blob: draft.file as File,
            groupId: draft.groupId,
            groupSize: draft.groupSize,
            error: draft.error,
          })
          .catch(() => {}),
      )

      // Загрузки стартуют сразу (до ожидания персиста) — состояние `uploading`
      // выставляется синхронно, как и раньше.
      const uploads = drafts
        .filter((draft) => draft.upload === 'pending')
        .map((draft) => upload(sessionId, draft.id))

      await Promise.all([...persistence, ...uploads])
    },

    remove(sessionId, id) {
      const list = get().bySession[sessionId] ?? []
      const target = list.find((item) => item.id === id)
      if (!target) return
      const removed = target.groupId
        ? list.filter((item) => item.groupId === target.groupId)
        : [target]
      const removedIds = new Set(removed.map((item) => item.id))
      const storage = getDraftAttachmentStorage()

      for (const item of removed) {
        abortUpload(sessionId, item.id)
        revokePreviewUrl(item)
        // Удаление из хранилища — best-effort, UI не ждёт.
        void storage.remove(sessionId, item.id).catch(() => {})
      }

      set((state) => {
        const next = (state.bySession[sessionId] ?? []).filter((item) => !removedIds.has(item.id))
        return { bySession: { ...state.bySession, [sessionId]: next } }
      })
    },

    clear(sessionId) {
      const list = get().bySession[sessionId] ?? []
      for (const item of list) {
        abortUpload(sessionId, item.id)
        revokePreviewUrl(item)
      }
      void getDraftAttachmentStorage().removeSession(sessionId).catch(() => {})
      set((state) => ({ bySession: { ...state.bySession, [sessionId]: [] } }))
    },

    async retry(sessionId, id) {
      const list = get().bySession[sessionId] ?? []
      const item = list.find((entry) => entry.id === id)
      if (!item || item.upload !== 'error') return
      // У восстановленного из истории элемента нет `File` — загружать нечего;
      // состояние (уже готовое вложение) не трогаем.
      if (!item.file) return

      // Причину-лимит повторно проверяем: файл мог уменьшиться/освободиться место.
      const limits = get().limits
      if (item.file.size > limits.maxBytesPerFile) {
        patch(sessionId, id, { upload: 'error', error: 'too_large' })
        return
      }
      if (item.error === 'too_many') {
        const active = list.filter((entry) => entry.id !== id && entry.upload !== 'error').length
        if (active >= limits.maxFilesPerMessage) {
          patch(sessionId, id, { upload: 'error', error: 'too_many' })
          return
        }
      }

      await upload(sessionId, id)
    },

    restore(sessionId, items) {
      if (items.length === 0) return
      const maxFiles = get().limits.maxFilesPerMessage
      set((state) => {
        const list = state.bySession[sessionId] ?? []
        const next = list.slice()
        // Дедуп по blob-пути: одно и то же вложение не должно появиться дважды
        // (например, повторный «Повторить» того же сообщения).
        const seen = new Set(
          next.map((item) => item.blobPath).filter((path): path is string => !!path),
        )
        for (const item of items) {
          if (next.length >= maxFiles) break
          if (seen.has(item.blobPath)) continue
          seen.add(item.blobPath)
          next.push({
            id: createLocalId(),
            name: item.name ?? item.blobPath.split('/').pop() ?? item.blobPath,
            mimeType: item.mimeType,
            size: item.size,
            previewUrl: item.previewUrl ?? '',
            upload: 'ready',
            // Серверный id вложения равен blob-пути (`POST /sessions/:id/attachments`).
            partId: item.blobPath,
            blobPath: item.blobPath,
          })
        }
        if (next.length === list.length) return state
        return { bySession: { ...state.bySession, [sessionId]: next } }
      })
    },

    readyPartIds(sessionId) {
      return (get().bySession[sessionId] ?? [])
        .filter((item) => item.upload === 'ready' && !!item.partId)
        .map((item) => item.partId as string)
    },

    hasPending(sessionId) {
      return (get().bySession[sessionId] ?? []).some(
        (item) => item.upload === 'pending' || item.upload === 'uploading',
      )
    },

    hasFailed(sessionId) {
      return (get().bySession[sessionId] ?? []).some((item) => item.upload === 'error')
    },

    async hydrate(sessionId) {
      // Один раз на сессию: повторные монтирования композера не перечитывают
      // хранилище и не плодят object URL.
      if (hydrated.has(sessionId)) return
      hydrated.add(sessionId)

      const records = await getDraftAttachmentStorage()
        .list(sessionId)
        .catch(() => [])
      if (records.length === 0) return

      const existing = get().bySession[sessionId] ?? []
      const maxFiles = get().limits.maxFilesPerMessage
      const known = new Set(existing.map((item) => item.id))
      const restored: DraftAttachment[] = []

      for (const record of records) {
        if (known.has(record.draftId)) continue
        if (existing.length + restored.length >= maxFiles) break

        const mimeType = record.mimeType || 'application/octet-stream'
        let file: File | undefined
        try {
          file = new File([record.blob], record.name, { type: mimeType })
        } catch {
          // Окружение без конструктора File — восстановим без байтов (retry не сработает).
          file = undefined
        }

        restored.push({
          id: record.draftId,
          file,
          name: record.name,
          mimeType,
          size: record.size,
          previewUrl: createPreviewUrlFromBlob(record.blob, mimeType),
          groupId: record.groupId,
          groupSize: record.groupSize,
          // Готовое вложение важнее ошибки: после частичного сбоя повторной
          // загрузки быть не должно.
          upload: record.partId ? 'ready' : record.error ? 'error' : 'pending',
          error: record.partId ? undefined : record.error,
          partId: record.partId,
          blobPath: record.blobPath,
        })
      }

      if (restored.length === 0) return

      set((state) => ({
        bySession: {
          ...state.bySession,
          [sessionId]: [...(state.bySession[sessionId] ?? existing), ...restored],
        },
      }))

      // Незавершённые загрузки дозапускаем (fire-and-forget): пользователь
      // не должен вручную жать «Повторить» после reload.
      for (const item of restored) {
        if (item.upload === 'pending') void upload(sessionId, item.id)
      }
    },

    dispose(sessionId) {
      const list = get().bySession[sessionId] ?? []
      for (const item of list) {
        abortUpload(sessionId, item.id)
        revokePreviewUrl(item)
      }
      hydrated.delete(sessionId)
      set((state) => {
        const next = { ...state.bySession }
        delete next[sessionId]
        return { bySession: next }
      })
    },
  }
})
